import React, { FC, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';

import { IdValidationConfigPreset, OrganizationUpdateRequestDTOTalentUniqueFieldsItem } from '@/api/generated';
import BackdropLoader from '@/components/BackdropLoader';
import ErrorAccessDenied from '@/components/ErrorAccessDenied/ErrorAccessDenied';
import ErrorGeneric from '@/components/ErrorGeneric/ErrorGeneric';
import ChipSelect from '@/components/fields/ChipSelect';
import Field, { FieldOnChangeHandler } from '@/components/fields/Field';
import Slider from '@/components/fields/Slider';
import TextInput from '@/components/fields/TextInput';
import { PermissionGuard } from '@/components/PermissionGuard';
import { useIsMidotUser } from '@/hooks/permissions/useIsMidotUser';
import { useOrganizationPermissions } from '@/hooks/permissions/useOrganizationPermissions';
import { PermissionKeys } from '@/hooks/permissions/usePermissions';
import { ReadonlyProvider } from '@/hooks/useReadonly';
import typography from '@/theme/typography';

import { getIdFieldsOptions, getIdStructureOptions, getValidationTypesOptions } from './authOptions';
import { getMonthsYearsScalesByDay } from './dateOptions';
import NormsList from './NormsList';
import { OrganizationSettingsFieldValues } from './types';

const IdentificationNormsTab: FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { spacing } = useTheme();
  const { watch, setValue } = useFormContext<OrganizationSettingsFieldValues>();
  const { organizationSlug } = useParams() as { organizationSlug: string };

  const { value: { canUpdateIdentificationAndNorms } = {} } = useOrganizationPermissions({
    variables: { organizationSlug }
  });

  const { isMidotUser } = useIsMidotUser();

  const talentUniqueFields = watch('talentUniqueFields');
  const idValidationPreset = watch('idValidation.preset');
  const isCustomPreset = idValidationPreset === IdValidationConfigPreset.CUSTOM;

  const hasIdNumber = useMemo(
    () => talentUniqueFields?.includes(OrganizationUpdateRequestDTOTalentUniqueFieldsItem.ID_NUMBER),
    [talentUniqueFields]
  );

  const handleUniqueFieldChange: FieldOnChangeHandler<[OrganizationUpdateRequestDTOTalentUniqueFieldsItem[]]> =
    useCallback(
      (value: OrganizationUpdateRequestDTOTalentUniqueFieldsItem[]) => {
        if (!value.includes(OrganizationUpdateRequestDTOTalentUniqueFieldsItem.ID_NUMBER)) return;

        setValue('idValidation.maxLength', 10);
      },
      [setValue]
    );

  const dateScales = useMemo(() => getMonthsYearsScalesByDay(t), [t]);

  return (
    <PermissionGuard
      all={[PermissionKeys.OrganizationcanViewIdentificationAndNorms]}
      variables={{ organizationSlug }}
      accessDeniedFallback={<ErrorAccessDenied />}
      errorFallback={<ErrorGeneric />}
      loadingFallback={<BackdropLoader />}
    >
      <ReadonlyProvider readonly={!isMidotUser || !canUpdateIdentificationAndNorms}>
        <Stack
          width="100%"
          sx={{
            '& .MuiFormLabel-root': { textAlign: 'start' },
            '& .MuiFormControl-root': { pt: 0, px: 0 },
            '& .DropdownSelect-expandMoreIcon': { color: 'text.primary' }
          }}
        >
          <Stack alignItems="start" justifyContent="space-between" width="100%" p={3} gap={3}>
            <Typography sx={{ ...typography.body1, fontWeight: typography.fontWeightMedium }}>
              {t('orgSettings.tabs.identificationNorms')}
            </Typography>
            <Typography sx={{ ...typography.body2 }}>{t('orgSettings.sections.identification')}</Typography>
          </Stack>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: spacing(2) }} mx={3}>
            <Field
              component={ChipSelect}
              name="talentUniqueFields"
              label={t('orgSettings.fields.idFields')}
              innerProps={{
                options: getIdFieldsOptions(t),
                sx: {
                  '& .MuiChip-label': { textTransform: 'none' }
                },
                enforcedValue: true
              }}
              withFormControl
            />
          </Box>
          {hasIdNumber && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: spacing(2) }} mx={3}>
              <Field
                component={ChipSelect}
                name="idValidation.preset"
                label={t('orgSettings.fields.validationTypes')}
                onChange={handleUniqueFieldChange}
                innerProps={{
                  options: getValidationTypesOptions(t),
                  enforcedValue: true
                }}
                withFormControl
              />
              {isCustomPreset && (
                <>
                  <Field
                    component={ChipSelect}
                    name="idValidation.structure"
                    label={t('orgSettings.fields.idStructure')}
                    innerProps={{
                      options: getIdStructureOptions(t),
                      enforcedValue: true
                    }}
                    withFormControl
                  />
                  <Field
                    component={TextInput}
                    name="idValidation.maxLength"
                    label={t('orgSettings.fields.maxChars')}
                    innerProps={{
                      sx: {
                        width: 40,
                        '& .MuiInputBase-input::-webkit-inner-spin-button, & .MuiInputBase-input::-webkit-outer-spin-button':
                          {
                            '-webkit-appearance': 'none',
                            margin: 0
                          }
                      },
                      InputProps: {
                        type: 'number'
                      }
                    }}
                    withFormControl
                  />
                </>
              )}
            </Box>
          )}
          <Divider sx={{ mx: 3 }} orientation="horizontal" />
          <Stack alignItems="start" justifyContent="space-between" width="100%" p={3}>
            <Typography sx={{ ...typography.body2 }}>{t('orgSettings.sections.validation')}</Typography>
          </Stack>
          <Stack maxWidth={370} mx={3}>
            <Field
              component={Slider}
              name="testValidityDays"
              label={t('orgSettings.fields.accessValidFor')}
              innerProps={{
                min: 30,
                max: 3650,
                valueLabelDisplay: 'auto',
                scales: dateScales,
                disabled: !isMidotUser
              }}
              withFormControl
            />
          </Stack>
          <Divider sx={{ mx: 3 }} orientation="horizontal" />
          <Stack alignItems="start" justifyContent="space-between" width="100%" p={3}>
            <Typography variant="body2">{t('orgSettings.sections.norms')}</Typography>
          </Stack>
          <NormsList />
        </Stack>
      </ReadonlyProvider>
    </PermissionGuard>
  );
};

export default IdentificationNormsTab;
